import { useEffect, useMemo, useRef } from 'react';
import { Outlet } from 'react-router-dom';
import { useAuthStore } from '@/shared/stores/auth';
import { canView } from '@/shared/lib/permissions';
import { RouteErrorBoundary } from '@/components/ui/ErrorBoundary';
import { Sidebar, type NavSection } from '@/components/layout/Sidebar';
import { Topbar } from '@/components/layout/Topbar';
import { ToastHost, toast } from '@/components/ui/Toast';
import { IncomingCallModal } from './components/IncomingCallModal';
import { MicBlockedModal } from './components/MicBlockedModal';
import { WrapupOverlay } from './components/WrapupOverlay';
import { FloatingSoftphone } from './components/FloatingSoftphone';
import { PersistentAudioBar } from '@/components/layout/PersistentAudioBar';
import { CustomerCard } from './components/CustomerCard';
import { DialerBar } from './components/DialerBar';
import { useSipStore } from './stores/sipStore';
import { useSipRegistration } from './stores/useSipRegistration';
import { useCustomerCard } from './stores/customerCard';
import { useCustomerCardPop } from './hooks/useCustomerCardPop';
import { useWrapupSettings } from './stores/wrapupSettings';
import { useHeartbeat } from '@/shared/hooks/useHeartbeat';
import { LIVENESS_MS, usePresenceLiveness } from '@/shared/hooks/usePresenceLiveness';
import { useAgentLive } from './hooks/useAgentLive';
import { ActiveCallBanner } from './components/ActiveCallBanner';
import { legacyAgentScreensEnabled } from './lib/legacyScreens';

/**
 * שלוש הלשוניות של האפיון (פרקים 3.1 ו-5). זה התפריט שהנציג רואה כברירת
 * מחדל — לצד הסופטפון הקבוע וכפתור התותח, שאינם פריטי תפריט.
 */
const NAV: NavSection[] = [
  {
    he: 'תפעול',
    items: [
      // 08/09/2026 (דורון): הנציג נוחת על הקמפיינים שלו — עד עכשיו הם היו
      // קבורים במסך שהוסתר מהתפריט, והוא לא ידע לאילו קמפיינים הוא משויך.
      { id: 'dialer', to: '/agent/campaigns', he: 'הקמפיינים שלי', icon: 'target' },
      { id: 'my_leads', to: '/agent/my-leads', he: 'בטיפולי', icon: 'users' },
      { id: 'callbacks', to: '/agent/callbacks', he: 'שיחות מעקב', icon: 'clock' },
      { id: 'search', to: '/agent/search', he: 'חיפוש', icon: 'search' },
      // אפיון מסך הנציג (07/09/2026): "מסך ביצועים אישי (חלקי)" — הנתונים
      // שלו בלבד, בלי ייצוא. ההרשאה stats כבר קיימת בקטלוג.
      { id: 'stats', to: '/agent/performance', he: 'הביצועים שלי', icon: 'chart' },
    ],
  },
];

/**
 * מפתח ההרשאה שכל פריט תפריט נבדק מולו — ברירת המחדל היא ה-id עצמו.
 * my_leads ו-search נוספו ל-AGENT_SCREENS בשרת, ולכן הם נבדקים ישירות
 * ואין יותר צורך במיפוי חלופי.
 */
// dialer_legacy נשען על אותה הרשאה כמו מסך הקמפיינים.
const PERMISSION_KEY: Record<string, string> = { dialer_legacy: 'dialer' };

/**
 * המסכים שמחוץ לאפיון. הנתיבים והקוד נשארים חיים; הם רק מוסתרים מהתפריט
 * אלא אם דגל המסכים הישנים דלוק — ראה lib/legacyScreens.ts.
 */
const LEGACY_NAV: NavSection[] = [
  {
    he: 'תפעול (ישן)',
    items: [
      { id: 'softphone', to: '/agent/softphone', he: 'סופטפון', icon: 'phone' },
      { id: 'active', to: '/agent/active', he: 'שיחה פעילה', icon: 'phoneCall' as never },
      { id: 'dialer_legacy', to: '/agent/dialer', he: 'קמפיין יוצא (ישן)', icon: 'target' },
    ],
  },
  {
    he: 'נתונים',
    items: [
      { id: 'history', to: '/agent/history', he: 'היסטוריית שיחות', icon: 'history' },
      { id: 'contacts', to: '/agent/contacts', he: 'אנשי קשר', icon: 'users' },
      { id: 'voicemail', to: '/agent/voicemail', he: 'תא קולי', icon: 'voicemail' },
    ],
  },
  {
    he: 'אישי',
    items: [
      // תסריטים = לשונית בהגדרות; סטטיסטיקה בוטלה; התראות עברו לפעמון ב-Topbar.
      { id: 'tasks', to: '/agent/tasks', he: 'משימות', icon: 'check' },
      { id: 'tickets', to: '/agent/tickets', he: 'פניות תמיכה', icon: 'info' },
      { id: 'shifts', to: '/agent/shifts', he: 'משמרות', icon: 'calendar' },
      { id: 'settings', to: '/agent/settings', he: 'הגדרות', icon: 'settings' },
      { id: 'help', to: '/agent/help', he: 'עזרה', icon: 'info' },
    ],
  },
];

export function AgentLayout() {
  useSipRegistration('agent');
  // סינון תפריט הנציג לפי הרשאות התפקיד (RBAC דינמי) — ids של הפריטים
  // תואמים למפתחות AGENT_SCREENS בקטלוג ההרשאות בשרת.
  const user = useAuthStore((s) => s.user);
  const nav = useMemo<NavSection[]>(
    () => {
      // ברירת המחדל היא שלוש הלשוניות של האפיון. המסכים הישנים נשארים
      // מנותבים ועובדים — הם רק לא מוצגים בתפריט אלא אם הדגל דלוק.
      const sections = legacyAgentScreensEnabled() ? [...NAV, ...LEGACY_NAV] : NAV;
      return sections
        .map((section) => ({
          ...section,
          items: section.items.filter((it) => canView(user, PERMISSION_KEY[it.id] ?? it.id)),
        }))
        .filter((section) => section.items.length > 0);
    },
    [user],
  );
  // Continuous presence heartbeat while the agent tab is open — without this the
  // server's 90s stale-sweep marks a logged-in agent "offline" even though they
  // are registered and available (they just never manually picked a status).
  // 15ש: "מחובר" בניטור החי נשען על פעימה ב-40ש האחרונות (01/10/2026).
  useHeartbeat(LIVENESS_MS);
  usePresenceLiveness({ ping: false });
  // שיחות נכנסות/פעילות לפי ה-PBX (SSE) — גם כשהדפדפן לא מנהל אותן.
  useAgentLive();

  // כרטיס הלקוח נפתח אוטומטית בשיחה — הלוגיקה ב-hook משותף כדי שגם
  // admin/superadmin יקבלו אותו (הסופטפון הצף זמין בכל הפרסונות).
  useCustomerCardPop();

  // Load the business's wrap-up display mode once, so the choice (modal /
  // sidebar / off) is known before the first call ends.
  useEffect(() => {
    void useWrapupSettings.getState().load();
  }, []);

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      <a href="#main-content" className="skip-link">דלג לתוכן</a>
      <Sidebar brand="" sections={nav} />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <Topbar title="נציג" />
        <ActiveCallBanner />
        <DialerBar />
        <main id="main-content" aria-label="תוכן ראשי" style={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <RouteErrorBoundary>
            <Outlet />
          </RouteErrorBoundary>
        </main>
      </div>
      <IncomingCallModal />
      <MicBlockedModal />
      <WrapupOverlay />
      <CustomerCard />
      <FloatingSoftphone />
      <PersistentAudioBar />
      <ToastHost />
    </div>
  );
}
